import { observer } from "mobx-react";
import {
  DoneStateUIState,
  HeaderUIState,
  ReadyUIState,
  SignDocumentPageUIState,
  signingFonts,
  SignWithEmailUIState,
  SignWithHandwritingUIState,
} from "./signDocumentDomain";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { isMobileView } from "../utils/screen";
import { BottomSpacer, Box, Card, Container, Grid, GridItem, VBox } from "../components-v2/Box";
import { dcompanyFrontpageUrl } from "../utils/navigation";
import { Button, LinkButton } from "../components-v2/Button";
import LogoImg from "../assets/img/brand/logoPrimary.svg";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Icon } from "../components-v2/Icon";
import { EmptyState } from "../components-v2/EmptyState";
import { getWindowDimensions } from "../utils/other";
import { PdfPreview } from "../components-v2/PdfPreview";
import parse from "html-react-parser";
import { useEffect, useState } from "react";
import { Alert } from "../components-v2/Alert";
import { TextInput } from "../components-v2/TextInput";
import AlertMessageStore from "../stores/AlertMessageStore";
import { ReactSketchCanvas } from "react-sketch-canvas";
import WebFont from "webfontloader";
import { RadioButtonGroup } from "../components-v2/RadioButton";
import { Select } from "../components-v2/Select";
import styles from "./signingComponents.module.css";
import { useTranslation } from "../stores/RootStoreContext";
import { isProductionEnvironment } from "../utils/environments";
import { Markdown } from "../text/Markdown";
import { useSize } from "../components-v2/useSize";
import { LandingPageHeader } from "../common/landingPageComponents";

const AppLogo = () => {
  const { lang } = useTranslation();
  return (
    <a href={dcompanyFrontpageUrl(lang)}>
      <img src={LogoImg} alt="dCompany logo" style={{ maxWidth: 200 }} />
    </a>
  );
};

export const Header = observer(({ title, links }: HeaderUIState) => {
  const { t } = useTranslation();
  return isMobileView() ? (
    <Box justifyContent={"center"} borderBottom bg>
      <div>
        <AppLogo />
      </div>
    </Box>
  ) : (
    <Box justifyContent={"space-between"} borderBottom bg padding>
      <Box alignItems={"center"}>
        <AppLogo />
        <div>{t(title)}</div>
      </Box>
      <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
        {links.map(({ id, ...props }) => (
          <LinkButton key={id} {...props} />
        ))}
      </Box>
    </Box>
  );
});

export const Header2 = observer((props: HeaderUIState) => <LandingPageHeader {...props.header} />);

export const ReadyView = observer(
  ({ title, description, document, startSigning }: ReadyUIState) => {
    const { t } = useTranslation();
    return (
      <>
        <VBox textAlign={"center"} padding>
          <strong>{t(title)}</strong>
          <span>{t(description)}</span>
        </VBox>
        {document && (
          <Box justifyContent={"center"}>
            {document.type === "pdf" && <PdfPreview file={document} />}
            {document.type === "html" && parse(document.content, { trim: true })}
          </Box>
        )}
        <BottomSpacer />
        <Box sticky={"bottom"} justifyContent={"center"} padding bg borderTop className={"tw-z-10"}>
          <Button variant={"primary"} className={"sm:tw-min-w-96"} {...startSigning} />
        </Box>
      </>
    );
  },
);

export const ReadyView2 = observer(
  ({
    header,
    document,
    startSigning,
    authorName,
    companyName,
    alertStore,
  }: ReadyUIState & { alertStore: AlertMessageStore }) => {
    const { t } = useTranslation();
    const { setRef, size } = useSize();
    return (
      <FullScreenLayout
        alertStore={alertStore}
        appBar={<Header2 {...header} />}
        centerPanel={
          <VBox container maxWidth="xl">
            <Grid grow gap={"lg"} marginY={"lg"}>
              <GridItem xs={12} lg={8}>
                <div ref={setRef}>
                  {document?.type === "pdf" && <PdfPreview file={document} width={size.width} />}
                </div>
              </GridItem>
              <GridItem xs={12} lg={4}>
                <Card title={{ key: "signing.document-details.title" }}>
                  <Markdown
                    value={t("signing.document-details.text", { authorName, companyName })}
                  />
                </Card>
              </GridItem>
            </Grid>
          </VBox>
        }
        footerPanel={
          <Box justifyContent={"center"} padding bg borderTop>
            <Button variant={"primary"} className={"sm:tw-min-w-96"} {...startSigning} />
          </Box>
        }
      />
    );
  },
);

export const DoneView = observer(
  ({ iconName, iconColor, title, description, primaryAction }: DoneStateUIState) => (
    <Box grow={"golden"} alignItems={"center"}>
      <EmptyState
        illustration={<Icon name={iconName} size={"jumbo"} color={iconColor} />}
        title={title}
        description={description}
        actions={
          primaryAction && (
            <Button
              variant={"primary"}
              label={primaryAction.label}
              onClick={primaryAction.onClick}
            />
          )
        }
      />
    </Box>
  ),
);

export const SignWithEmailView = observer(
  ({ message, verificationCode, submitVerificationCode }: SignWithEmailUIState) => (
    <Container maxWidth={"sm"} marginTop={"md"}>
      <Alert
        icon={{ size: "lg", name: "warning", weight: "regular" }}
        type={"info"}
        message={message}
      />
      <TextInput {...verificationCode} />
      <Button variant={"primary"} {...submitVerificationCode} />
    </Container>
  ),
);

export const SignWithHandwritingView = observer(
  ({
    message,
    inputMode,
    font,
    typedText,
    submit,
    setReactSketchCanvasRef,
  }: SignWithHandwritingUIState) => {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
      const handleResize = () => {
        setWindowDimensions(getWindowDimensions());
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      WebFont.load({
        google: {
          families: signingFonts,
        },
      });
    }, []);

    return (
      <Container maxWidth={"lg"}>
        <VBox marginTop={"md"} alignItems={"center"}>
          <Alert icon={{ name: "warning", size: "lg" }} type={"info"} message={message} />
          <RadioButtonGroup horizontal={!isMobileView()} {...inputMode} />
          {inputMode.selected === "type" ? (
            <>
              <Select {...font} />
              <input
                className={styles.textInput}
                style={{ fontFamily: `'${font.value}', cursive` }}
                {...typedText}
              />
            </>
          ) : (
            <ReactSketchCanvas
              ref={setReactSketchCanvasRef}
              className={styles.drawCanvas}
              width="100%"
              height="400px"
              strokeWidth={windowDimensions.width / 350}
              strokeColor="darkblue"
              exportWithBackgroundImage={false}
            />
          )}
          {submit && <Button variant={"primary"} {...submit} />}
        </VBox>
      </Container>
    );
  },
);

export const SignDocumentPageView = observer(
  ({ state, alertStore }: { state: SignDocumentPageUIState; alertStore: AlertMessageStore }) => {
    const header = isProductionEnvironment() ? (
      <Header {...state.header} />
    ) : (
      <Header2 {...state.header} />
    );
    switch (state.type) {
      case "loading":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={header}
            centerPanel={<LoadingIndicator fullscreen />}
          />
        );
      case "ready":
        return isProductionEnvironment() ? (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={<Header {...state.header} />}
            centerPanel={<ReadyView {...state} />}
          />
        ) : (
          <ReadyView2 alertStore={alertStore} {...state} />
        );
      case "sign-with-email":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={header}
            centerPanel={<SignWithEmailView {...state} />}
          />
        );
      case "sign-with-handwriting":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={header}
            centerPanel={<SignWithHandwritingView {...state} />}
          />
        );
      case "done":
        return (
          <FullScreenLayout
            alertStore={alertStore}
            appBar={header}
            centerPanel={<DoneView {...state} />}
          />
        );
    }
  },
);
